/**
 * Query key factories shared across hook modules.
 *
 * These live apart from the hook files so cross-cutting code (cache
 * invalidation in `userScopedQueries`, SSR hydrators) can build keys without
 * pulling each hook's endpoint modules and adapters into its bundle.
 */

import type { SearchListingsRequest } from '@/src/lib/api/endpoints/search';
import type { UserListingType } from '@/src/lib/api/endpoints/user';

export const hpToursKeys = {
  all: ['hpTours'] as const,
  scheduledToursForUser: ['hpTours', 'scheduledToursForUser'] as const,
};

export const inquiryKeys = {
  all: ['inquiry'] as const,
  messageCache: () => [...inquiryKeys.all, 'messageCache'] as const,
};

export const listingDetailKeys = {
  all: ['listingDetail'] as const,
  byMaloneLotId: (maloneLotIdEncoded: string, type: 'unit' | 'building', unit?: string | null) =>
    ['listingDetail', 'byMaloneLotId', maloneLotIdEncoded, type, normalizeListingDetailUnit(unit) ?? ''] as const,
};

export function normalizeListingDetailUnit(unit: string | null | undefined): string | null {
  const normalizedUnit = unit?.trim();
  return normalizedUnit || null;
}

export const searchKeys = {
  all: ['search'] as const,
  listings: (request: SearchListingsRequest) => ['search', 'listings', request] as const,
  // Separate namespace: these entries cache a bare unit count, not a
  // SearchListingsResult, so they must never collide with `listings`.
  unitCount: (request: SearchListingsRequest) => ['search', 'unitCount', request] as const,
};

export const recentSearchKeys = {
  all: ['recentSearch'] as const,
  search: () => [...recentSearchKeys.all, 'search'] as const,
};

export const renterProfileKeys = {
  all: ['renterProfile'] as const,
  get: () => [...renterProfileKeys.all, 'get'] as const,
};

export const savedSearchKeys = {
  all: ['savedSearch'] as const,
  list: () => [...savedSearchKeys.all, 'list'] as const,
};

export const userListingKeys = {
  all: ['userListings'] as const,
  byType: (type: UserListingType) => [...userListingKeys.all, type] as const,
};
